import {
    Add, ArrowDropDown, Info, OpenInNew, Remove,
} from '@mui/icons-material';
import {
    Box,
    TextField,
    Typography,
    styled,
    Button,
    Tooltip,
    Chip,
    Skeleton,
    IconButton,
    Menu,
    MenuItem,
} from '@mui/material';
import { ChangeEvent, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRecoilValue } from 'recoil';
import { TemplateDiscoveryService } from 'src/services/TemplateDiscoveryService';
import { CopyButton } from 'src/components/common/CopyButton';
import { getDesignTypeIcon, getDesignTypeColor } from 'src/utils/designTypeUtils';
import { DesignCreationType } from 'src/services/DesignConductorService';
import { isAuthorizedQuery } from 'src/selectors/isAuthorizedQuery';
import { DocumentSourceQuery } from 'src/hooks/useDocumentSource';
import { SourceProductOptions, SourceProductOptionsMap } from 'src/components/MatchingVisualizer/SourceProductOptions';

type DocumentSourceInputsProps = {
    documentUri: string;
    pdsResult: string;
    showPdsResult: boolean;
    dpcId: string;
    showDpcId: boolean;
    designConceptId: string;
    showDesignConceptId: boolean;
    brandId: string;
    showBrandId: boolean;
    sourceProductKey: string;
    sourceProductVersion: number | undefined;
    sourceProductOptions: SourceProductOptionsMap;
    showSourceProduct: boolean;
    documentSourceQuery: DocumentSourceQuery;
    onDocumentUriChange?: (uri: string) => void;
    onPdsResultChange?: (uri: string) => void;
    onShowPdsResultChange?: (show: boolean) => void;
    onDpcIdChange?: (id: string) => void;
    onShowDpcIdChange?: (show: boolean) => void;
    onDesignConceptIdChange?: (id: string) => void;
    onShowDesignConceptIdChange?: (show: boolean) => void;
    onBrandIdChange?: (id: string) => void;
    onShowBrandIdChange?: (show: boolean) => void;
    onSourceProductKeyChange?: (key: string) => void;
    onSourceProductVersionChange?: (version: number | undefined) => void;
    onSourceProductOptionsChange?: (opts: SourceProductOptionsMap) => void;
    onShowSourceProductChange?: (show: boolean) => void;
};

const Container = styled(Box)(({ theme }) => ({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(3),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(2),
}));

const SiteResultTooltipText = 'Add a Site Result (PDS Result) URI to see the preview in the right column for comparison purposes. It will not affect the matching process.';

const PANEL_SOURCES = {
    TEMPLATE_TOKEN: 'TEMPLATE_TOKEN',
    DYNAMIC_DESIGN: 'DYNAMIC_DESIGN',
    AUTOLAYOUT_DESIGN: 'AUTOLAYOUT_DESIGN',
};

const IDENTIFYING_PANEL_SOURCES: string[] = [
    PANEL_SOURCES.TEMPLATE_TOKEN,
    PANEL_SOURCES.DYNAMIC_DESIGN,
    PANEL_SOURCES.AUTOLAYOUT_DESIGN,
];

const findIdentifyingPanel = (documentSource: any): { source: string; data: unknown } | null => {
    const panels = documentSource?.metadata?.documentSources?.panels as Array<{ source: string; data: unknown }> | undefined;

    if (!panels) return null;
    return panels.find((p) => IDENTIFYING_PANEL_SOURCES.includes(p.source)) ?? null;
};

const getDesignCreationType = (documentSource: any): DesignCreationType | null => {
    const panel = findIdentifyingPanel(documentSource);

    if (!panel) return null;
    if (panel.source === PANEL_SOURCES.TEMPLATE_TOKEN) return 'Static';
    if (panel.source === PANEL_SOURCES.DYNAMIC_DESIGN) return 'Dynamic';
    if (panel.source === PANEL_SOURCES.AUTOLAYOUT_DESIGN) return 'AutoLayout';
    return null;
};

const getNonStaticDesignConcept = (documentSource: any): string | null => {
    const panel = findIdentifyingPanel(documentSource);

    if (!panel) return null;
    if ((panel.source === PANEL_SOURCES.DYNAMIC_DESIGN || panel.source === PANEL_SOURCES.AUTOLAYOUT_DESIGN) && typeof panel.data === 'object' && panel.data !== null) {
        return `${(panel.data as { designConceptId?: string }).designConceptId}`;
    }
    return null;
};

const getTemplateToken = (documentSource: any): string | null => {
    const panel = findIdentifyingPanel(documentSource);

    if (!panel || panel.source !== PANEL_SOURCES.TEMPLATE_TOKEN) return null;
    return panel.data as string;
};

export const DocumentSourceInputs = ({
    documentUri,
    pdsResult,
    showPdsResult,
    dpcId,
    showDpcId,
    designConceptId,
    showDesignConceptId,
    brandId,
    showBrandId,
    sourceProductKey,
    sourceProductVersion,
    sourceProductOptions,
    showSourceProduct,
    documentSourceQuery,
    onDocumentUriChange,
    onPdsResultChange,
    onShowPdsResultChange,
    onDpcIdChange,
    onShowDpcIdChange,
    onDesignConceptIdChange,
    onShowDesignConceptIdChange,
    onBrandIdChange,
    onShowBrandIdChange,
    onSourceProductKeyChange,
    onSourceProductVersionChange,
    onSourceProductOptionsChange,
    onShowSourceProductChange,
}: DocumentSourceInputsProps): JSX.Element => {
    const accessToken = useRecoilValue(isAuthorizedQuery);

    // Use the passed-in document source result instead of querying it ourselves
    const { data: documentSource, isLoading: isLoadingDocumentSource } = documentSourceQuery;

    const designType = getDesignCreationType(documentSource);
    const isStaticDesign = designType === 'Static';
    const templateToken = getTemplateToken(documentSource);
    const templateIdentifier = documentSource?.templateIdentifier?.value;

    // Query template discovery for static designs to get the design concept ID
    const { data: staticDesignConceptId, isLoading: isLoadingTemplateDiscovery } = useQuery({
        queryKey: ['templateDiscovery', templateToken, accessToken],
        queryFn: async () => {
            if (!templateToken || !accessToken) {
                throw new Error('Missing template token or access token');
            }

            const ensembleLines = await TemplateDiscoveryService.getEnsembleLinesFromTemplateToken(
                templateToken,
                accessToken,
            );

            return ensembleLines[0]?.ensembles[0]?.designProperties?.isProminentColorVariant !== undefined
                ? ensembleLines[0].designConceptId
                : null;
        },
        enabled: isStaticDesign && !!templateToken && !!accessToken && !!documentSource,
        retry: false,
        refetchOnWindowFocus: false,
        suspense: false,
    });

    const designConcept = isStaticDesign
        ? staticDesignConceptId
        : getNonStaticDesignConcept(documentSource);

    const isLoading = isLoadingDocumentSource || (isStaticDesign && isLoadingTemplateDiscovery);

    const [addAnchor, setAddAnchor] = useState<HTMLElement | null>(null);

    type TogglableField = {
        key: string;
        menuLabel: string;
        sectionTitle: string;
        tooltip: string;
        show: boolean;
        onShow: (show: boolean) => void;
        onRemove: () => void;
        render: () => JSX.Element;
    };

    const dpcIdTooltip = 'Provide a DPC ID to call DPCS and reuse an existing personalization context. Ignored if a Document Source or Brand Kit is also provided.';
    const designConceptIdTooltip = 'Used as a fallback. If a Document Source is provided, its extracted design concept ID takes priority.';
    const brandIdTooltip = 'Provide a Brand Kit ID to extract a personalization context via DPCM. Ignored if a Document Source is also provided.';
    const sourceProductTooltip = 'Optional. Provide the source product to enable resizer matching.';

    const togglableFields: TogglableField[] = [
        {
            key: 'dpcId',
            menuLabel: 'DPC ID',
            sectionTitle: 'Design Personalization Context ID',
            tooltip: dpcIdTooltip,
            show: showDpcId,
            onShow: (v): void => onShowDpcIdChange?.(v),
            onRemove: (): void => { onDpcIdChange?.(''); onShowDpcIdChange?.(false); },
            render: (): JSX.Element => (
                <TextField
                    fullWidth
                    label="DPC ID"
                    placeholder="DPC ID"
                    size="small"
                    value={dpcId}
                    onChange={(e: ChangeEvent<HTMLInputElement>): void => onDpcIdChange?.(e.target.value)}
                />
            ),
        },
        {
            key: 'designConceptId',
            menuLabel: 'Design Concept ID',
            sectionTitle: 'Design Concept ID',
            tooltip: designConceptIdTooltip,
            show: showDesignConceptId,
            onShow: (v): void => onShowDesignConceptIdChange?.(v),
            onRemove: (): void => { onDesignConceptIdChange?.(''); onShowDesignConceptIdChange?.(false); },
            render: (): JSX.Element => (
                <TextField
                    fullWidth
                    label="Design Concept ID"
                    placeholder="e.g. 45153"
                    size="small"
                    value={designConceptId}
                    onChange={(e: ChangeEvent<HTMLInputElement>): void => onDesignConceptIdChange?.(e.target.value)}
                />
            ),
        },
        {
            key: 'brandId',
            menuLabel: 'Brand Kit ID',
            sectionTitle: 'Brand Kit ID',
            tooltip: brandIdTooltip,
            show: showBrandId,
            onShow: (v): void => onShowBrandIdChange?.(v),
            onRemove: (): void => { onBrandIdChange?.(''); onShowBrandIdChange?.(false); },
            render: (): JSX.Element => (
                <TextField
                    fullWidth
                    label="Brand Kit ID"
                    placeholder="Brand Kit ID"
                    size="small"
                    value={brandId}
                    onChange={(e: ChangeEvent<HTMLInputElement>): void => onBrandIdChange?.(e.target.value)}
                />
            ),
        },
        {
            key: 'sourceProduct',
            menuLabel: 'Source Product',
            sectionTitle: 'Source Product',
            tooltip: sourceProductTooltip,
            show: showSourceProduct,
            onShow: (v): void => onShowSourceProductChange?.(v),
            onRemove: (): void => {
                onSourceProductKeyChange?.('');
                onSourceProductVersionChange?.(undefined);
                onSourceProductOptionsChange?.({});
                onShowSourceProductChange?.(false);
            },
            render: (): JSX.Element => (
                <SourceProductOptions
                    options={sourceProductOptions}
                    productKey={sourceProductKey}
                    version={sourceProductVersion}
                    onChangeOptions={(opts): void => onSourceProductOptionsChange?.(opts)}
                    onChangeProductKey={(val): void => onSourceProductKeyChange?.(val)}
                    onChangeVersion={(val): void => onSourceProductVersionChange?.(val)}
                />
            ),
        },
        {
            key: 'siteResult',
            menuLabel: 'Site Result',
            sectionTitle: 'Site Result',
            tooltip: SiteResultTooltipText,
            show: showPdsResult,
            onShow: (v): void => onShowPdsResultChange?.(v),
            onRemove: (): void => { onPdsResultChange?.(''); onShowPdsResultChange?.(false); },
            render: (): JSX.Element => (
                <TextField
                    fullWidth
                    label="Site Result URI"
                    placeholder="https://..."
                    size="small"
                    value={pdsResult}
                    onChange={(e: ChangeEvent<HTMLInputElement>): void => onPdsResultChange?.(e.target.value)}
                />
            ),
        },
    ];

    const hiddenTogglableFields = togglableFields.filter((f) => !f.show);

    return (
        <Container>
            <Box>
                <Typography gutterBottom fontWeight="medium" variant="subtitle1">
                    Document Source
                </Typography>
                <TextField
                    fullWidth
                    label="Document Source URI"
                    placeholder="https://storage.documents.cimpress.io/..."
                    size="small"
                    value={documentUri}
                    onChange={(e: ChangeEvent<HTMLInputElement>): void => onDocumentUriChange?.(e.target.value)}
                />
                {documentUri && isLoading && (
                    <Box sx={{ mt: 2 }}>
                        <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 1, mb: 1,
                        }}
                        >
                            <Typography color="text.secondary" variant="body2">
                                Design Creation Type:
                            </Typography>
                            <Skeleton animation="wave" height={32} width={120} />
                        </Box>
                        <Box sx={{
                            display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1,
                        }}
                        >
                            <Typography color="text.secondary" sx={{ flexShrink: 0 }} variant="body2">
                                Template Identifier:
                            </Typography>
                            <Skeleton animation="wave" height={24} width={200} />
                        </Box>
                        <Box sx={{
                            display: 'flex', alignItems: 'flex-start', gap: 1,
                        }}
                        >
                            <Typography color="text.secondary" sx={{ flexShrink: 0 }} variant="body2">
                                Design Concept:
                            </Typography>
                            <Skeleton animation="wave" height={24} width={80} />
                        </Box>
                    </Box>
                )}
                {documentUri && !isLoading && designType && (
                    <Box sx={{ mt: 2 }}>
                        <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 1, mb: 1,
                        }}
                        >
                            <Typography color="text.secondary" variant="body2">
                                Design Creation Type:
                            </Typography>
                            <Chip
                                color={getDesignTypeColor(designType)}
                                icon={getDesignTypeIcon(designType)}
                                label={`${designType} Design`}
                                size="small"
                            />
                        </Box>
                        {templateIdentifier && (
                            <Box sx={{
                                display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1,
                            }}
                            >
                                <Typography color="text.secondary" sx={{ flexShrink: 0 }} variant="body2">
                                    Template Identifier:
                                </Typography>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    minWidth: 0,
                                    flex: 1,
                                    marginTop: '1px',
                                    gap: 2,
                                }}
                                >
                                    <Typography
                                        sx={{
                                            fontFamily: 'monospace',
                                            fontSize: '0.875rem',
                                            wordBreak: 'break-all',
                                            minWidth: 0,
                                        }}
                                        variant="body2"
                                    >
                                        {templateIdentifier}
                                    </Typography>
                                    <CopyButton
                                        size="small"
                                        textToCopy={templateIdentifier}
                                        tooltipTitle="Copy template identifier"
                                    />
                                </Box>
                            </Box>
                        )}
                        {designConcept && (
                            <Box sx={{
                                display: 'flex', alignItems: 'flex-start', gap: 1,
                            }}
                            >
                                <Typography color="text.secondary" sx={{ flexShrink: 0 }} variant="body2">
                                    Design Concept:
                                </Typography>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    minWidth: 0,
                                    flex: 1,
                                    marginTop: '1px',
                                    gap: 2,
                                }}
                                >
                                    <Typography
                                        sx={{
                                            fontFamily: 'monospace',
                                            fontSize: '0.875rem',
                                            wordBreak: 'break-all',
                                            minWidth: 0,
                                        }}
                                        variant="body2"
                                    >
                                        {designConcept}
                                    </Typography>
                                    <CopyButton
                                        size="small"
                                        textToCopy={designConcept}
                                        tooltipTitle="Copy design concept"
                                    />
                                    <Tooltip title="View design concept in Tagging Tool">
                                        <IconButton
                                            sx={{
                                                padding: 0.5,
                                                color: 'text.secondary',
                                                width: 16,
                                                height: 16,
                                                '& .MuiSvgIcon-fontSizeSmall': {
                                                    fontSize: '0.875rem',
                                                },
                                            }}
                                            onClick={(): void => window.open(`https://tagging.gallery.vpsvc.com/?designConceptId=${designConcept}`, '_blank')?.focus()}
                                        >
                                            <OpenInNew sx={{ fontSize: '16px', color: 'text.secondary' }} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
            {togglableFields.filter((f) => f.show).map((field) => (
                <Box key={field.key}>
                    <Box sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5,
                    }}
                    >
                        <Typography fontWeight="medium" sx={{ display: 'flex', alignItems: 'center' }} variant="subtitle1">
                            {field.sectionTitle}
                            <Tooltip title={field.tooltip}>
                                <Info sx={{
                                    ml: 1, height: 16, width: 16, color: 'primary.main',
                                }}
                                />
                            </Tooltip>
                        </Typography>
                        <Tooltip title={`Remove ${field.menuLabel}`}>
                            <IconButton aria-label={`remove ${field.menuLabel}`} size="small" onClick={field.onRemove}>
                                <Remove fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    {field.render()}
                </Box>
            ))}
            {hiddenTogglableFields.length > 0 && (
                <Box>
                    <Button
                        color="secondary"
                        endIcon={<ArrowDropDown />}
                        size="small"
                        startIcon={<Add />}
                        variant="outlined"
                        onClick={(e): void => setAddAnchor(e.currentTarget)}
                    >
                        Add Input
                    </Button>
                    <Menu
                        anchorEl={addAnchor}
                        open={!!addAnchor}
                        onClose={(): void => setAddAnchor(null)}
                    >
                        {hiddenTogglableFields.map((field) => (
                            <MenuItem
                                key={field.key}
                                onClick={(): void => {
                                    field.onShow(true);
                                    setAddAnchor(null);
                                }}
                            >
                                {field.menuLabel}
                            </MenuItem>
                        ))}
                    </Menu>
                </Box>
            )}
        </Container>
    );
};
